import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth, adminFetch } from "./auth";

function AdminShell({ title, subtitle, children }) {
  return (
    <div className="admin-auth-wrap">
      <div className="admin-auth-card" data-testid="admin-auth-card">
        <Link to="/" className="admin-auth-brand">
          Redland Golf &amp; Country Club
        </Link>
        <h1>{title}</h1>
        {subtitle ? <p className="admin-auth-sub">{subtitle}</p> : null}
        {children}
      </div>
    </div>
  );
}

export function ProtectedRoute({ children, allowChange = false }) {
  const { admin } = useAuth();
  if (admin === undefined) {
    return <div className="admin-loading">Loading…</div>;
  }
  if (admin === null) {
    return <Navigate to="/admin" replace />;
  }
  if (admin.must_change_password && !allowChange) {
    return <Navigate to="/admin/change-password" replace />;
  }
  return children;
}

export function AdminLogin() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (admin && !admin.must_change_password) return <Navigate to="/admin/dashboard" replace />;
  if (admin && admin.must_change_password) return <Navigate to="/admin/change-password" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const me = await login(username, password);
      navigate(me.must_change_password ? "/admin/change-password" : "/admin/dashboard", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell title="Admin Login" subtitle="Sign in to manage website content.">
      <form onSubmit={submit} className="admin-form" data-testid="admin-login-form">
        {error ? <div className="admin-alert admin-alert-error" data-testid="admin-login-error">{error}</div> : null}
        <label>
          Username
          <input data-testid="admin-username-input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required />
        </label>
        <label>
          Password
          <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        </label>
        <button className="button button-primary" type="submit" disabled={busy} data-testid="admin-login-submit">
          {busy ? "Signing in…" : "Sign In"}
        </button>
        <Link className="admin-link" to="/admin/forgot" data-testid="admin-forgot-link">
          Forgot your password?
        </Link>
      </form>
    </AdminShell>
  );
}

export function AdminChangePassword() {
  const { admin, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const needsSecurity = admin ? !admin.has_security_question : true;
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "", security_question: "", security_answer: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        current_password: form.current_password,
        new_password: form.new_password,
      };
      if (needsSecurity) {
        payload.security_question = form.security_question;
        payload.security_answer = form.security_answer;
      }
      await adminFetch("/api/admin/change-password", { method: "POST", json: payload });
      await refresh();
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell
      title="Set a New Password"
      subtitle={needsSecurity ? "For security, create a new password and set a security question." : "Update your password."}
    >
      <form onSubmit={submit} className="admin-form" data-testid="admin-change-form">
        {error ? <div className="admin-alert admin-alert-error" data-testid="admin-change-error">{error}</div> : null}
        <label>
          Current password
          <input data-testid="change-current-input" type="password" value={form.current_password} onChange={set("current_password")} autoComplete="current-password" required />
        </label>
        <label>
          New password
          <input data-testid="change-new-input" type="password" value={form.new_password} onChange={set("new_password")} autoComplete="new-password" required />
        </label>
        <label>
          Confirm new password
          <input data-testid="change-confirm-input" type="password" value={form.confirm} onChange={set("confirm")} autoComplete="new-password" required />
        </label>
        <p className="admin-hint">At least 8 characters, with a mix of letters and numbers.</p>
        {needsSecurity ? (
          <>
            <label>
              Security question
              <input data-testid="change-question-input" value={form.security_question} onChange={set("security_question")} placeholder="e.g. What was the name of your first pet?" required />
            </label>
            <label>
              Security answer
              <input data-testid="change-answer-input" value={form.security_answer} onChange={set("security_answer")} required />
            </label>
          </>
        ) : null}
        <button className="button button-primary" type="submit" disabled={busy} data-testid="admin-change-submit">
          {busy ? "Saving…" : "Save New Password"}
        </button>
        <button type="button" className="admin-link" onClick={() => { logout(); navigate("/admin"); }} data-testid="admin-change-cancel">
          Cancel and sign out
        </button>
      </form>
    </AdminShell>
  );
}

export function AdminForgot() {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await adminFetch("/api/admin/forgot-password", { method: "POST", json: { username } });
      setQuestion(data.security_question);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirm) {
      setError("New password and confirmation do not match.");
      return;
    }
    setBusy(true);
    try {
      const data = await adminFetch("/api/admin/reset-password", {
        method: "POST",
        json: { username, security_answer: answer, new_password: newPassword },
      });
      setSuccess(data.message || "Password reset. You can now sign in.");
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell title="Reset Password" subtitle="Answer your security question to set a new password.">
      {error ? <div className="admin-alert admin-alert-error" data-testid="admin-forgot-error">{error}</div> : null}
      {step === 1 ? (
        <form onSubmit={lookup} className="admin-form" data-testid="admin-forgot-form">
          <label>
            Username
            <input data-testid="forgot-username-input" value={username} onChange={(e) => setUsername(e.target.value)} required />
          </label>
          <button className="button button-primary" type="submit" disabled={busy} data-testid="forgot-lookup-submit">
            {busy ? "Checking…" : "Continue"}
          </button>
          <Link className="admin-link" to="/admin">Back to login</Link>
        </form>
      ) : null}
      {step === 2 ? (
        <form onSubmit={reset} className="admin-form" data-testid="admin-reset-form">
          <p className="admin-question" data-testid="admin-security-question">{question}</p>
          <label>
            Your answer
            <input data-testid="reset-answer-input" value={answer} onChange={(e) => setAnswer(e.target.value)} required />
          </label>
          <label>
            New password
            <input data-testid="reset-new-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" required />
          </label>
          <label>
            Confirm new password
            <input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />
          </label>
          <button className="button button-primary" type="submit" disabled={busy} data-testid="reset-submit">
            {busy ? "Saving…" : "Reset Password"}
          </button>
        </form>
      ) : null}
      {step === 3 ? (
        <div className="admin-form">
          <div className="admin-alert admin-alert-success" data-testid="admin-reset-success">{success}</div>
          <Link className="button button-primary" to="/admin">Go to Login</Link>
        </div>
      ) : null}
    </AdminShell>
  );
}
